import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutoComplete, Button, Col, DatePicker, Form, Image, Input, InputNumber, Modal, Radio, Row,
  Select, Space, Tag, TreeSelect, Typography, Upload, message
} from 'antd';
import { DeleteOutlined, EyeOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { buildBudgetOrgTreeSelectProps } from '../../utils/orgTree';
import { buildExpenseClaimSubject } from '../../utils/expenseClaimSubject';
import {
  assertExpenseCompanyMatchesInvoices,
  assertSupplierPayeeMatchesInvoices,
  collectExpenseCompanyNamesFromOption,
  formatInvoiceBuyerDisplayText,
  formatInvoiceIssuerDisplayText
} from '../../utils/expenseClaimSupplierValidate';
import {
  createPayeeFieldValidator,
  validateBankAccountNo,
  validateChineseIdCard,
  validateSupplierTaxNo
} from '../../utils/expensePayeeFieldValidate';
import { amountToChinese } from '../../utils/moneyChinese';
import { isImageUploadPath, isPdfUploadPath, resolveUploadUrl } from '../../utils/uploadUrl';
import RelatedApprovalNoField from './RelatedApprovalNoField';
import './ExpenseClaimForm.css';

const { Text } = Typography;

const PAYEE_TYPE_OPTIONS = [
  { value: 'employee', label: '员工' },
  { value: 'external_person', label: '外部个人' },
  { value: 'supplier', label: '供应商' }
];

const INVOICE_TYPE_OPTIONS = [
  { value: 'special', label: '专票' },
  { value: 'general', label: '普票' },
  { value: 'other', label: '其他' }
];

const URGENCY_OPTIONS = [
  { value: 'normal', label: '正常' },
  { value: 'urgent', label: '紧急' }
];

const INVOICE_OCR_TIMEOUT_MS = 40000;

/** 与后端 expClaimService.MULTI_DEPT_BUDGET_ORG_CODE 保持一致 */
const MULTI_DEPT_BUDGET_ORG_CODE = '__MULTI_DEPT__';

function isMultiDeptBudgetLine(line) {
  return String(line?.budget_org_code || '').trim() === MULTI_DEPT_BUDGET_ORG_CODE;
}

function createEmptyBudgetSplit() {
  return { org_code: undefined, amount: undefined };
}

function isInvoiceOcrAbortError(error) {
  return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
}

function getExpenseOccurrenceMonthBounds() {
  const reference = dayjs();
  const min = reference.subtract(1, 'year').month(11).startOf('month');
  const max = reference.endOf('month');
  return { min, max };
}

function isExpenseOccurrenceMonthDisabled(current) {
  if (!current) return false;
  const date = dayjs(current);
  if (!date.isValid()) return false;
  const { min, max } = getExpenseOccurrenceMonthBounds();
  return date.isBefore(min, 'month') || date.isAfter(max, 'month');
}

function validateExpenseOccurrenceMonthValue(value) {
  if (!value) return null;
  const date = dayjs(value);
  if (!date.isValid()) return null;
  const { min, max } = getExpenseOccurrenceMonthBounds();
  if (date.isBefore(min, 'month') || date.isAfter(max, 'month')) {
    return `费用产生时间须在上年度12月至本月之间（${min.format('YYYY-MM')} 至 ${max.format('YYYY-MM')}）`;
  }
  return null;
}

function resolveBudgetPeriodLabel(periodType, yearMonth) {
  if (!periodType || !yearMonth) return '';
  const text = dayjs(yearMonth).isValid()
    ? dayjs(yearMonth).format('YYYY-MM')
    : String(yearMonth).slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(text)) return '';
  const year = Number(text.slice(0, 4));
  const month = Number(text.slice(5, 7));
  if (periodType === 'annual') return `${year}年度`;
  if (periodType === 'quarterly') return `${year}年第${Math.ceil(month / 3)}季度`;
  return `${year}年${month}月`;
}

const COL3 = 8;
const COL2 = 12;
const FULL = 24;
const GUTTER = [16, 8];

function FieldCol({ span = COL3, children }) {
  return <Col xs={24} md={span}>{children}</Col>;
}

function FormSection({ title, children }) {
  return (
    <section className="expense-claim-section">
      <div className="expense-claim-section-title">{title}</div>
      {children}
    </section>
  );
}

/** 供 buildExpenseClaimPayload 读取（不写入 Form，避免循环引用 / 数组绑定 Input 警告） */
const expenseCompanyOptionsRef = { current: [] };
const expenseCompanyNamesRef = { current: [] };

export function setExpenseCompanyNamesSnapshot(names) {
  if (!names) {
    expenseCompanyNamesRef.current = [];
    return;
  }
  expenseCompanyNamesRef.current = Array.isArray(names) ? [...names] : [names];
}

/** 复杂对象字段占位：不把 object 传给 DOM input，Form 内部仍保留原值 */
function PreserveField({ value: _value, onChange, onBlur, id }) {
  return <input type="hidden" id={id} onChange={onChange} onBlur={onBlur} readOnly aria-hidden="true" />;
}

function InvoiceFilePreviewModal({ file, onClose }) {
  if (!file?.file_path) return null;
  const url = resolveUploadUrl(file.file_path);
  const isImage = isImageUploadPath(file.file_path || file.file_name);
  const isPdf = isPdfUploadPath(file.file_path || file.file_name);

  return (
    <Modal
      open
      title={file.file_name || '发票预览'}
      onCancel={onClose}
      width={isPdf ? 920 : 760}
      destroyOnHidden
      footer={[
        <Button key="open" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
          新窗口打开
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>关闭</Button>
      ]}
    >
      {isImage && (
        <Image src={url} alt={file.file_name} style={{ maxWidth: '100%' }} preview={false} />
      )}
      {isPdf && (
        <iframe
          src={url}
          title={file.file_name}
          style={{ width: '100%', height: '70vh', border: 0 }}
        />
      )}
      {!isImage && !isPdf && (
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          该格式暂不支持内嵌预览，请
          {' '}
          <a href={url} target="_blank" rel="noreferrer">下载查看</a>
        </Typography.Paragraph>
      )}
    </Modal>
  );
}

function isInvoiceAttachmentRequired(invoiceType) {
  return invoiceType === 'special' || invoiceType === 'general';
}

function InvoiceVatVerifyHint({ verify, invoiceNo }) {
  if (!verify?.required) return null;

  const colorMap = {
    success: 'success',
    warning: 'warning',
    failed: 'error',
    error: 'error',
    skipped: 'default'
  };
  const summary = verify.summary || verify.message || '发票验真';
  const detailParts = [];
  if (verify.detail?.saler_name) detailParts.push(verify.detail.saler_name);
  const resolvedInvoiceNo = String(invoiceNo || '').trim();
  if (resolvedInvoiceNo) detailParts.push(`发票号 ${resolvedInvoiceNo}`);
  const detailText = detailParts.join(' · ');

  return (
    <div className="expense-line-vat-verify">
      <Tag color={colorMap[verify.status] || 'default'} className="expense-line-vat-verify-tag">
        {summary}
      </Tag>
      {verify.status === 'success' && detailText ? (
        <Text type="secondary" className="expense-line-vat-verify-detail">{detailText}</Text>
      ) : null}
      {verify.status !== 'success' && verify.message ? (
        <Text type="secondary" className="expense-line-vat-verify-detail">{verify.message}</Text>
      ) : null}
    </div>
  );
}

function InvoiceBuyerHint({ ocrRaw, verify, hasInvoiceFile }) {
  const buyerName = formatInvoiceBuyerDisplayText({ ocrRaw, verify });
  if (buyerName) {
    return (
      <div className="expense-line-buyer-hint">
        <Text type="secondary" className="expense-line-vat-verify-detail">
          我方单位：{buyerName}
        </Text>
      </div>
    );
  }
  if (!hasInvoiceFile) return null;

  return (
    <div className="expense-line-buyer-hint expense-line-buyer-hint--error">
      <Text type="danger" className="expense-line-vat-verify-detail">
        未能识别我方单位名称，请重新上传发票
      </Text>
    </div>
  );
}

function InvoiceIssuerHint({ verify, ocrRaw, invoiceNo }) {
  if (verify?.required) {
    return <InvoiceVatVerifyHint verify={verify} invoiceNo={invoiceNo} />;
  }

  const detailText = formatInvoiceIssuerDisplayText({ ocrRaw, invoiceNo, verify });
  if (!detailText) return null;

  return (
    <div className="expense-line-vat-verify">
      <Text type="secondary" className="expense-line-vat-verify-detail">{detailText}</Text>
    </div>
  );
}

function ExpenseLineAttachment({
  value = [],
  fieldName,
  loading,
  uploadDisabled = false,
  uploadLabel = '上传识别',
  onUpload,
  onRemoveFile,
  onPreview
}) {
  if (value.length) {
    return (
      <div className="expense-line-attachment">
        {value.map((file, fileIndex) => (
          <div key={`${file.file_path}-${fileIndex}`} className="expense-line-attachment-file">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              aria-label="预览发票"
              className="expense-line-attachment-eye"
              onClick={() => onPreview(file)}
            />
            <Typography.Text
              ellipsis={{ tooltip: file.file_name || '发票' }}
              className="expense-line-attachment-name"
            >
              {file.file_name || '发票'}
            </Typography.Text>
            <Button
              type="link"
              size="small"
              danger
              className="expense-line-attachment-remove"
              onClick={() => onRemoveFile(fieldName, fileIndex)}
            >
              删除
            </Button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="expense-line-attachment">
      <Upload
        beforeUpload={(file) => onUpload(file, fieldName)}
        showUploadList={false}
        maxCount={1}
      >
        <Button icon={<UploadOutlined />} loading={loading} className="expense-line-attachment-upload">
          {uploadLabel}
        </Button>
      </Upload>
    </div>
  );
}

function lineInvoiceFilesRule(fieldName) {
  return ({ getFieldValue }) => ({
    validator: (_, value) => {
      const invoiceType = getFieldValue(['lines', fieldName, 'invoice_type']);
      const files = value || getFieldValue(['lines', fieldName, 'invoice_files']) || [];
      if (isInvoiceAttachmentRequired(invoiceType) && !files.length) {
        return Promise.reject(new Error('专票/普票须上传发票'));
      }
      return Promise.resolve();
    }
  });
}

function lineInvoiceBuyerRule(fieldName) {
  return ({ getFieldValue }) => ({
    validator: (_, value) => {
      const invoiceType = getFieldValue(['lines', fieldName, 'invoice_type']);
      if (!isInvoiceAttachmentRequired(invoiceType)) return Promise.resolve();
      const files = value || getFieldValue(['lines', fieldName, 'invoice_files']) || [];
      if (!files.length) return Promise.resolve();
      const line = getFieldValue(['lines', fieldName]) || {};
      const buyerName = formatInvoiceBuyerDisplayText({
        ocrRaw: line.ocr_raw,
        verify: line.vat_verify
      });
      if (!buyerName) {
        return Promise.reject(new Error('未能识别购买方（我方单位）名称，请重新上传发票'));
      }
      return Promise.resolve();
    }
  });
}

function isInvoiceAmountFieldVisible(invoiceType) {
  return invoiceType === 'special' || invoiceType === 'general' || invoiceType === 'other';
}

function hasFilledInvoiceAmount(value) {
  return value != null && value !== '' && Number.isFinite(Number(value));
}

function shouldCompareInvoiceActualAmount(invoiceType, invoiceAmount) {
  if (isInvoiceAttachmentRequired(invoiceType)) return true;
  return invoiceType === 'other' && hasFilledInvoiceAmount(invoiceAmount);
}

function lineInvoiceAmountRule(fieldName) {
  return ({ getFieldValue }) => ({
    validator: (_, value) => {
      const invoiceType = getFieldValue(['lines', fieldName, 'invoice_type']);
      if (!isInvoiceAmountFieldVisible(invoiceType)) return Promise.resolve();
      if (isInvoiceAttachmentRequired(invoiceType)) {
        if (value == null || value === '') return Promise.reject(new Error('请填写发票金额'));
      } else if (value == null || value === '') {
        return Promise.resolve();
      }
      const invoiceAmount = Number(value);
      if (!Number.isFinite(invoiceAmount) || invoiceAmount < 0) {
        return Promise.reject(new Error('发票金额无效'));
      }
      const actualAmount = Number(getFieldValue(['lines', fieldName, 'actual_amount']));
      if (shouldCompareInvoiceActualAmount(invoiceType, invoiceAmount)
        && Number.isFinite(actualAmount)
        && actualAmount > invoiceAmount + 0.009) {
        return Promise.reject(new Error('发票金额不能小于实付金额'));
      }
      return Promise.resolve();
    }
  });
}

function lineActualAmountRule(fieldName) {
  return ({ getFieldValue }) => ({
    validator: (_, value) => {
      if (value == null || value === '') return Promise.resolve();
      const invoiceType = getFieldValue(['lines', fieldName, 'invoice_type']);
      const invoiceAmount = Number(getFieldValue(['lines', fieldName, 'invoice_amount']));
      const actualAmount = Number(value);
      if (shouldCompareInvoiceActualAmount(invoiceType, invoiceAmount)
        && actualAmount > invoiceAmount + 0.009) {
        return Promise.reject(new Error('实付金额不能大于发票金额'));
      }
      return Promise.resolve();
    }
  });
}

function lineActualExceedsInvoice(line) {
  const invoiceType = String(line?.invoice_type || '').trim();
  if (!shouldCompareInvoiceActualAmount(invoiceType, line?.invoice_amount)) return false;
  const invoiceAmount = Number(line.invoice_amount);
  const actualAmount = Number(line?.actual_amount);
  if (!Number.isFinite(actualAmount)) return false;
  return actualAmount > invoiceAmount + 0.009;
}

function lineHasAmountBelowInvoice(line) {
  const invoiceType = String(line?.invoice_type || '').trim();
  if (!isInvoiceAttachmentRequired(invoiceType)) return false;
  const invoiceAmount = Number(line?.invoice_amount);
  const actualAmount = Number(line?.actual_amount);
  if (!Number.isFinite(invoiceAmount) || !Number.isFinite(actualAmount)) return false;
  return actualAmount + 0.009 < invoiceAmount;
}

function findOrgName(orgs, code) {
  if (!code) return '';
  const hit = orgs.find((o) => o.org_code === code);
  return hit?.org_name || code;
}

export function migrateExpenseClaimLines(formData = {}, defaultBudgetOrgCode) {
  const headerOrg = formData.budget_org_code || defaultBudgetOrgCode;
  const headerSubitem = formData.subitem_id;
  return (formData.lines || []).map((line) => {
    const budgetOrgCode = line.budget_org_code || line.cost_org_code || headerOrg;
    const migrated = {
      ...line,
      budget_org_code: budgetOrgCode,
      subitem_id: line.subitem_id ?? headerSubitem ?? undefined
    };
    if (isMultiDeptBudgetLine(migrated)) {
      const splits = Array.isArray(line.budget_splits) ? line.budget_splits : [];
      migrated.budget_splits = splits.length >= 2
        ? splits
        : [createEmptyBudgetSplit(), createEmptyBudgetSplit()];
    }
    return migrated;
  });
}

function budgetSplitsListRule(lineIndex) {
  return ({ getFieldValue }) => ({
    validator: async () => {
      const line = getFieldValue(['lines', lineIndex]) || {};
      if (!isMultiDeptBudgetLine(line)) return;
      const splits = line.budget_splits || [];
      if (splits.length < 2) {
        throw new Error('多部门分摊至少添加两个部门');
      }
      const orgCodes = splits.map((item) => String(item?.org_code || '').trim()).filter(Boolean);
      if (orgCodes.length !== splits.length) {
        throw new Error('请为每个分摊项选择部门');
      }
      if (new Set(orgCodes).size !== orgCodes.length) {
        throw new Error('分摊部门不能重复');
      }
      const actualAmount = round2(line.actual_amount);
      if (!Number.isFinite(actualAmount) || actualAmount <= 0) return;
      const splitSum = round2(splits.reduce((sum, item) => sum + Number(item?.amount || 0), 0));
      if (Math.abs(splitSum - actualAmount) > 0.009) {
        throw new Error('分摊金额之和须等于实付金额');
      }
    }
  });
}

async function assertLinesBudgetAvailable(lines, categoryId, expenseOccurrenceMonth) {
  if (!categoryId || !expenseOccurrenceMonth) return;
  const month = expenseOccurrenceMonth?.format?.('YYYY-MM')
    ?? String(expenseOccurrenceMonth).slice(0, 7);
  const checks = [];
  (lines || []).forEach((line, index) => {
    if (isMultiDeptBudgetLine(line)) {
      (line.budget_splits || []).forEach((split, splitIndex) => {
        const orgCode = String(split?.org_code || '').trim();
        const amount = Number(split?.amount || 0);
        if (orgCode && amount > 0) {
          checks.push({ lineNo: index + 1, splitNo: splitIndex + 1, orgCode, amount });
        }
      });
      return;
    }
    const orgCode = String(line?.budget_org_code || '').trim();
    const amount = Number(line?.actual_amount || 0);
    if (orgCode && orgCode !== MULTI_DEPT_BUDGET_ORG_CODE && amount > 0) {
      checks.push({ lineNo: index + 1, splitNo: null, orgCode, amount });
    }
  });

  for (const item of checks) {
    const res = await api.get('/expense/claims/budget-balance', {
      params: {
        org_code: item.orgCode,
        category_id: categoryId,
        expense_occurrence_month: month
      }
    });
    if (!res.data?.success) continue;
    const available = Number(res.data.data?.amount_available || 0);
    if (item.amount > available + 0.009) {
      const prefix = item.splitNo
        ? `第 ${item.lineNo} 行第 ${item.splitNo} 个分摊部门`
        : `第 ${item.lineNo} 行`;
      throw new Error(`${prefix}分摊金额超过剩余预算（剩余 ¥${available.toFixed(2)}）`);
    }
  }
}

function LineBudgetBalance({ orgCode, categoryId, expenseOccurrenceMonth, actualAmount }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orgCode || !categoryId || !expenseOccurrenceMonth) {
      setBalance(null);
      return undefined;
    }
    const month = expenseOccurrenceMonth?.format?.('YYYY-MM')
      ?? String(expenseOccurrenceMonth).slice(0, 7);
    let cancelled = false;
    setLoading(true);
    api.get('/expense/claims/budget-balance', {
      params: {
        org_code: orgCode,
        category_id: categoryId,
        expense_occurrence_month: month
      }
    }).then((res) => {
      if (!cancelled && res.data.success) setBalance(res.data.data);
    }).catch(() => {
      if (!cancelled) setBalance(null);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [orgCode, categoryId, expenseOccurrenceMonth]);

  if (!orgCode || !categoryId || !expenseOccurrenceMonth) {
    return null;
  }
  if (loading) {
    return <Text type="secondary" className="expense-line-budget-hint">预算查询中…</Text>;
  }
  if (!balance) return null;

  const remaining = Number(balance.amount_available || 0);
  const lineAmount = Number(actualAmount || 0);
  const afterLine = round2(remaining - lineAmount);
  const insufficient = lineAmount > 0 && afterLine < -0.009;

  const label = `剩余预算 ¥${remaining.toFixed(2)}`;
  return (
    <Tag
      color={insufficient ? 'error' : 'processing'}
      className="expense-line-budget-tag"
      title={label}
    >
      {label}
    </Tag>
  );
}

function LineBudgetSplitRow({
  splitField,
  lineFieldName,
  splitIndex,
  canRemoveSplit,
  onRemoveSplit,
  budgetOrgTreeProps,
  categoryId,
  expenseOccurrenceMonth,
  form
}) {
  const orgCode = Form.useWatch(['lines', lineFieldName, 'budget_splits', splitField.name, 'org_code'], form);
  const splitAmount = Form.useWatch(['lines', lineFieldName, 'budget_splits', splitField.name, 'amount'], form);

  return (
    <div className="expense-line-split-row">
      <Form.Item
        name={[splitField.name, 'org_code']}
        label={splitIndex === 0 ? '分摊部门' : undefined}
        rules={[{ required: true, message: '请选择分摊部门' }]}
        className="expense-line-split-org"
      >
        <TreeSelect {...budgetOrgTreeProps} placeholder="选择部门" />
      </Form.Item>
      <Form.Item
        name={[splitField.name, 'amount']}
        label={splitIndex === 0 ? '分摊金额' : undefined}
        rules={[
          { required: true, message: '请填写分摊金额' },
          ({ getFieldValue }) => ({
            validator: async (_, value) => {
              const amount = Number(value || 0);
              if (!Number.isFinite(amount) || amount <= 0) return;
              const line = getFieldValue(['lines', lineFieldName]) || {};
              if (!isMultiDeptBudgetLine(line)) return;
              const month = expenseOccurrenceMonth?.format?.('YYYY-MM')
                ?? String(expenseOccurrenceMonth || '').slice(0, 7);
              const code = getFieldValue(['lines', lineFieldName, 'budget_splits', splitField.name, 'org_code']);
              if (!code || !categoryId || !month) return;
              const res = await api.get('/expense/claims/budget-balance', {
                params: {
                  org_code: code,
                  category_id: categoryId,
                  expense_occurrence_month: month
                }
              });
              if (!res.data?.success) return;
              const available = Number(res.data.data?.amount_available || 0);
              if (amount > available + 0.009) {
                throw new Error(`超过剩余预算 ¥${available.toFixed(2)}`);
              }
            }
          })
        ]}
        className="expense-line-split-amount"
      >
        <InputNumber min={0.01} precision={2} placeholder="0.00" style={{ width: '100%' }} />
      </Form.Item>
      <div className="expense-line-split-budget">
        <LineBudgetBalance
          orgCode={orgCode}
          categoryId={categoryId}
          expenseOccurrenceMonth={expenseOccurrenceMonth}
          actualAmount={splitAmount}
        />
      </div>
      {canRemoveSplit ? (
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          className="expense-line-split-remove"
          onClick={onRemoveSplit}
        />
      ) : (
        <span className="expense-line-split-remove-placeholder" />
      )}
    </div>
  );
}

function ExpenseLineCard({
  field,
  index,
  form,
  canRemove,
  onRemove,
  selectedCategory,
  budgetOrgTreeProps,
  budgetOrgTreePropsForSplit,
  categoryId,
  expenseOccurrenceMonth,
  ocrLoadingKey,
  onInvoiceUpload,
  onRemoveFile,
  onPreview,
  skipInvoiceBuyerValidation = false,
  subitemDisabled = false,
  lineContentDisabled = false
}) {
  const invoiceType = Form.useWatch(['lines', field.name, 'invoice_type'], form);
  const lines = Form.useWatch('lines', form) || [];
  const lineData = lines[field.name] || {};
  const budgetOrgCode = lineData.budget_org_code;
  const isMultiDept = isMultiDeptBudgetLine(lineData);
  const vatVerify = lineData.vat_verify;
  const ocrRaw = lineData.ocr_raw;
  const invoiceNo = lineData.invoice_no;
  const invoiceFieldsRequired = isInvoiceAttachmentRequired(invoiceType);

  return (
    <div className="expense-line-card">
      <div className="expense-line-card-header">
        <Text strong className="expense-line-card-title">费用明细 {index + 1}</Text>
        {canRemove && !lineContentDisabled ? (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          >
            删除
          </Button>
        ) : null}
      </div>

      <div className="expense-line-row expense-line-row-budget">
        <Form.Item
          name={[field.name, 'budget_org_code']}
          label="费用归属部门"
          rules={[{ required: true, message: '请选择费用归属部门' }]}
          className="expense-line-field"
        >
          <TreeSelect
            {...budgetOrgTreeProps}
            placeholder="选择部门"
            disabled={lineContentDisabled}
            onChange={(value) => {
              const linesValue = form.getFieldValue('lines') || [];
              const nextLines = [...linesValue];
              const line = { ...(nextLines[field.name] || {}) };
              if (value === MULTI_DEPT_BUDGET_ORG_CODE) {
                line.budget_splits = [
                  createEmptyBudgetSplit(),
                  createEmptyBudgetSplit()
                ];
              } else {
                line.budget_splits = undefined;
              }
              nextLines[field.name] = line;
              form.setFieldsValue({ lines: nextLines });
            }}
          />
        </Form.Item>
        <Form.Item
          name={[field.name, 'subitem_id']}
          label="预算二级科目"
          rules={selectedCategory?.sub_items?.length
            ? [{ required: true, message: '请选择二级科目' }]
            : []}
          className="expense-line-field"
        >
          <Select
            allowClear
            disabled={subitemDisabled || !selectedCategory?.sub_items?.length}
            placeholder={selectedCategory?.sub_items?.length ? '选择二级科目' : '无二级科目'}
            showSearch
            optionFilterProp="label"
            options={(selectedCategory?.sub_items || []).map((item) => ({
              value: item.id,
              label: item.name
            }))}
          />
        </Form.Item>
        <Form.Item
          name={[field.name, 'expense_detail']}
          label="费用明细"
          rules={[{ required: true, message: '请填写费用明细' }]}
          className="expense-line-field"
        >
          <Input placeholder="费用说明" disabled={lineContentDisabled} />
        </Form.Item>
      </div>

      {isMultiDept ? (
        <div className="expense-line-splits">
          <Text type="secondary" className="expense-line-splits-title">分摊明细（至少 2 个部门，金额之和须等于实付金额）</Text>
          <Form.List name={[field.name, 'budget_splits']}>
            {(splitFields, { add: addSplit, remove: removeSplit }) => (
              <>
                {splitFields.map((splitField, splitIndex) => (
                  <LineBudgetSplitRow
                    key={splitField.key}
                    splitField={splitField}
                    lineFieldName={field.name}
                    splitIndex={splitIndex}
                    canRemoveSplit={splitFields.length > 2}
                    onRemoveSplit={() => removeSplit(splitField.name)}
                    budgetOrgTreeProps={budgetOrgTreePropsForSplit}
                    categoryId={categoryId}
                    expenseOccurrenceMonth={expenseOccurrenceMonth}
                    form={form}
                  />
                ))}
                <div className="expense-line-splits-actions">
                  <Button
                    type="dashed"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => addSplit(createEmptyBudgetSplit())}
                  >
                    添加分摊部门
                  </Button>
                </div>
              </>
            )}
          </Form.List>
        </div>
      ) : null}

      <div className="expense-line-row expense-line-row-invoice">
        <div className="expense-line-col">
          <Form.Item
            name={[field.name, 'invoice_type']}
            label="发票类型"
            rules={[{ required: true, message: '请选择发票类型' }]}
            className="expense-line-field"
          >
            <Select
              options={INVOICE_TYPE_OPTIONS}
              onChange={(value) => {
                const linesValue = form.getFieldValue('lines') || [];
                const nextLines = [...linesValue];
                const line = { ...(nextLines[field.name] || {}) };
                if (value === 'other') {
                  line.invoice_no = undefined;
                  line.ocr_raw = null;
                  line.vat_verify = null;
                  nextLines[field.name] = line;
                  form.setFieldsValue({ lines: nextLines });
                }
                setTimeout(() => {
                  form.validateFields([
                    ['lines', field.name, 'invoice_files'],
                    ['lines', field.name, 'invoice_amount'],
                    ['lines', field.name, 'actual_amount']
                  ]).catch(() => {});
                }, 0);
              }}
            />
          </Form.Item>
        </div>
        <div className="expense-line-col expense-claim-lines-attachment-cell">
          <Form.Item
            name={[field.name, 'invoice_files']}
            label="发票附件"
            required={invoiceFieldsRequired}
            initialValue={[]}
            rules={[
              lineInvoiceFilesRule(field.name),
              ...(skipInvoiceBuyerValidation ? [] : [lineInvoiceBuyerRule(field.name)])
            ]}
            className="expense-line-field"
          >
            <ExpenseLineAttachment
              fieldName={field.name}
              loading={ocrLoadingKey === field.key}
              uploadDisabled={ocrLoadingKey != null && ocrLoadingKey !== field.key}
              uploadLabel={invoiceType === 'other' ? '上传附件' : '上传识别'}
              onUpload={(file, fieldName) => onInvoiceUpload(file, fieldName, field.key)}
              onRemoveFile={onRemoveFile}
              onPreview={onPreview}
            />
          </Form.Item>
          <Form.Item name={[field.name, 'invoice_no']} hidden noStyle>
            <Input hidden />
          </Form.Item>
          <Form.Item name={[field.name, 'vat_verify']} hidden noStyle>
            <PreserveField />
          </Form.Item>
          <Form.Item name={[field.name, 'ocr_raw']} hidden noStyle>
            <PreserveField />
          </Form.Item>
          {invoiceType !== 'other' ? (
            <>
              <InvoiceBuyerHint
                ocrRaw={ocrRaw}
                verify={vatVerify}
                hasInvoiceFile={Boolean(lineData.invoice_files?.length)}
              />
              <InvoiceIssuerHint verify={vatVerify} ocrRaw={ocrRaw} invoiceNo={invoiceNo} />
            </>
          ) : null}
        </div>
        {isInvoiceAmountFieldVisible(invoiceType) ? (
          <div className="expense-line-col">
            <Form.Item
              name={[field.name, 'invoice_amount']}
              label="发票金额"
              required={invoiceFieldsRequired}
              rules={[lineInvoiceAmountRule(field.name)]}
              className="expense-line-field"
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0.00"
                disabled={lineContentDisabled}
                onChange={() => {
                  setTimeout(() => {
                    form.validateFields([
                      ['lines', field.name, 'invoice_amount'],
                      ['lines', field.name, 'actual_amount']
                    ]).catch(() => {});
                  }, 0);
                }}
              />
            </Form.Item>
          </div>
        ) : null}
        <div className="expense-line-col expense-line-col-actual">
          <Form.Item label="实付金额" required className="expense-line-field expense-line-actual-wrap">
            <div className="expense-line-actual-input-row">
              <Form.Item
                name={[field.name, 'actual_amount']}
                noStyle
                rules={[
                  { required: true, message: '请填写实付金额' },
                  lineActualAmountRule(field.name),
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (value == null || value === '') return;
                      const line = getFieldValue(['lines', field.name]) || {};
                      if (!isMultiDeptBudgetLine(line)) return;
                      await budgetSplitsListRule(field.name)({ getFieldValue }).validator();
                    }
                  })
                ]}
              >
                <InputNumber
                  min={0.01}
                  precision={2}
                  placeholder="0.00"
                  disabled={lineContentDisabled}
                  onChange={() => {
                    setTimeout(() => {
                      const fieldsToValidate = [['lines', field.name, 'actual_amount']];
                      if (isInvoiceAmountFieldVisible(invoiceType)) {
                        fieldsToValidate.push(['lines', field.name, 'invoice_amount']);
                      }
                      if (isMultiDept) {
                        fieldsToValidate.push(['lines', field.name, 'budget_splits']);
                      }
                      form.validateFields(fieldsToValidate).catch(() => {});
                    }, 0);
                  }}
                />
              </Form.Item>
              <div className="expense-line-budget-slot">
                {!isMultiDept ? (
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const line = form.getFieldValue(['lines', field.name]) || {};
                      return (
                        <LineBudgetBalance
                          orgCode={line.budget_org_code}
                          categoryId={form.getFieldValue('category_id')}
                          expenseOccurrenceMonth={form.getFieldValue('expense_occurrence_month')}
                          actualAmount={line.actual_amount}
                        />
                      );
                    }}
                  </Form.Item>
                ) : null}
              </div>
            </div>
          </Form.Item>
        </div>
      </div>
    </div>
  );
}

function round2(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

export async function buildExpenseClaimPayload(form, submit, context = {}) {
  if (submit) {
    await form.validateFields();
  }
  // validateFields 只返回已注册字段，ocr_raw 等须用 getFieldsValue(true) 取全量
  const values = form.getFieldsValue(true);
  const lines = (values.lines || []).map((line) => ({
    expense_detail: line.expense_detail,
    budget_org_code: line.budget_org_code,
    budget_splits: isMultiDeptBudgetLine(line)
      ? (line.budget_splits || []).map((split) => ({
        org_code: split.org_code,
        org_name: split.org_name,
        amount: split.amount
      }))
      : undefined,
    subitem_id: line.subitem_id || null,
    invoice_type: line.invoice_type,
    invoice_files: line.invoice_files || [],
    invoice_no: line.invoice_no,
    invoice_amount: line.invoice_amount,
    actual_amount: line.actual_amount,
    ocr_raw: line.ocr_raw || null,
    vat_verify: line.vat_verify || null
  }));

  if (submit) {
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
      if (isMultiDeptBudgetLine(lines[lineIndex])) {
        await budgetSplitsListRule(lineIndex)({ getFieldValue: form.getFieldValue.bind(form) }).validator();
      }
    }
    await assertLinesBudgetAvailable(lines, values.category_id, values.expense_occurrence_month);
    if (!context.skipPayeeInvoiceMatch) {
      const supplierError = assertSupplierPayeeMatchesInvoices({
        payeeType: values.payee_type,
        payeeName: values.payee_name,
        taxNo: values.tax_no || context.payeeTaxNo,
        lines
      });
      if (supplierError) {
        throw new Error(supplierError);
      }
    }

    if (!context.skipExpenseCompanyInvoiceMatch) {
      const buyerError = assertExpenseCompanyMatchesInvoices({
        expenseCompanyId: values.expense_company_id,
        expenseCompanyOptions: context.expenseCompanyOptions || expenseCompanyOptionsRef.current,
        expenseCompanyNames: context.expenseCompanyNames || expenseCompanyNamesRef.current,
        lines
      });
      if (buyerError) {
        throw new Error(buyerError);
      }
    }

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
      if (lineActualExceedsInvoice(lines[lineIndex])) {
        throw new Error(`第 ${lineIndex + 1} 行实付金额不能大于发票金额`);
      }
    }
  }

  const budgetSubject = values.category_id
    ? `${values.category_id}::`
    : undefined;

  return {
    claim_type: 'daily',
    submit,
    expense_company_id: values.expense_company_id,
    applicant_dept_code: values.applicant_dept_code,
    category_id: values.category_id,
    expense_occurrence_month: values.expense_occurrence_month?.format?.('YYYY-MM')
      ?? values.expense_occurrence_month
      ?? null,
    budget_subject: budgetSubject,
    reason: values.reason,
    payee_type: values.payee_type,
    employee_no: values.payee_type === 'employee' ? values.payee_ref : undefined,
    payee_id: values.payee_type !== 'employee' && values.payee_ref ? values.payee_ref : undefined,
    payee_name: values.payee_name,
    bank_name: values.bank_name,
    bank_branch_name: values.bank_branch_name,
    bank_account_no: values.bank_account_no,
    id_card_no: values.id_card_no,
    tax_no: values.tax_no || context.payeeTaxNo,
    lines,
    other_attachments: values.other_attachments || [],
    urgency: values.urgency,
    remark: values.remark,
    related_approval_biz_type: values.related_approval_biz_type || undefined,
    related_approval_id: values.related_approval_id || undefined,
    related_approval_no: String(values.related_approval_no || '').trim() || undefined,
    related_inbound_ids: Array.isArray(values.related_inbound_ids)
      ? values.related_inbound_ids.filter(Boolean)
      : undefined
  };
}

export function applyMatInboundExpensePrefill(form, prefill, meta) {
  if (!prefill || typeof prefill !== 'object') return;
  const budgetOrg = prefill.budget_org_code || prefill.applicant_dept_code;
  const monthText = prefill.expense_occurrence_month
    ? String(prefill.expense_occurrence_month).slice(0, 7)
    : '';
  const lockedSubitemId = prefill.budget_subject_locked ? prefill.subitem_id : undefined;
  form.setFieldsValue({
    reason: prefill.reason,
    expense_occurrence_month: monthText ? dayjs(`${monthText}-01`) : undefined,
    payee_type: 'supplier',
    payee_ref: prefill.payee_id,
    payee_name: prefill.payee_name,
    bank_name: prefill.bank_name,
    bank_branch_name: prefill.bank_branch_name,
    bank_account_no: prefill.bank_account_no,
    tax_no: prefill.tax_no,
    applicant_dept_code: prefill.applicant_dept_code || budgetOrg,
    category_id: prefill.budget_subject_locked ? prefill.category_id : undefined,
    related_approval_no: prefill.related_approval_no,
    related_approval_biz_type: prefill.related_approval_biz_type,
    related_approval_id: undefined,
    related_inbound_ids: prefill.related_inbound_ids,
    inbound_total_amount: prefill.inbound_total_amount,
    expense_company_id: meta?.default_expense_company_id,
    urgency: 'normal',
    lines: [{
      expense_detail: '',
      budget_org_code: budgetOrg,
      subitem_id: lockedSubitemId,
      invoice_type: 'general',
      invoice_files: [],
      invoice_amount: undefined,
      actual_amount: undefined
    }],
    other_attachments: [],
    remark: ''
  });
}

export function applyBusinessTripExpensePrefill(form, prefill, meta) {
  if (!prefill || typeof prefill !== 'object') return;
  const defaultBudgetOrg = meta?.default_budget_org_code || meta?.default_applicant_dept_code;
  const monthText = prefill.expense_occurrence_month
    ? String(prefill.expense_occurrence_month).slice(0, 7)
    : '';
  const lockedSubitemId = prefill.budget_subject_locked ? prefill.subitem_id : undefined;
  form.setFieldsValue({
    reason: prefill.reason,
    expense_occurrence_month: monthText ? dayjs(`${monthText}-01`) : undefined,
    payee_type: 'employee',
    payee_ref: prefill.employee_no,
    payee_name: prefill.payee_name,
    bank_name: prefill.bank_name,
    bank_account_no: prefill.bank_account_no,
    related_approval_no: prefill.related_approval_no,
    related_approval_biz_type: prefill.related_approval_biz_type,
    related_approval_id: prefill.related_approval_id,
    expense_company_id: meta?.default_expense_company_id,
    applicant_dept_code: meta?.default_applicant_dept_code,
    category_id: prefill.budget_subject_locked ? prefill.category_id : undefined,
    urgency: 'normal',
    lines: [{
      expense_detail: '',
      budget_org_code: defaultBudgetOrg,
      subitem_id: lockedSubitemId,
      invoice_type: 'general',
      invoice_files: [],
      invoice_amount: undefined,
      actual_amount: undefined
    }],
    other_attachments: [],
    remark: ''
  });
}

function findCategoryById(categories, categoryId) {
  if (categoryId == null || categoryId === '') return null;
  return (categories || []).find((item) => String(item.id) === String(categoryId)) || null;
}

export function hydratePrepaymentAppendForm(form, payload, defaults = {}) {
  if (!payload || typeof payload !== 'object') return;
  const monthRaw = payload.expense_occurrence_month ?? defaults.expense_occurrence_month;
  const monthText = monthRaw ? String(monthRaw).slice(0, 7) : '';
  form.setFieldsValue({
    expense_company_id: payload.expense_company_id ?? defaults.expense_company_id,
    applicant_dept_code: payload.applicant_dept_code ?? defaults.applicant_dept_code,
    category_id: payload.category_id ?? defaults.category_id,
    reason: payload.reason ?? defaults.reason,
    urgency: payload.urgency || defaults.urgency || 'normal',
    expense_occurrence_month: monthText ? dayjs(`${monthText}-01`) : undefined,
    payee_type: payload.payee_type ?? defaults.payee_type,
    payee_name: payload.payee_name ?? defaults.payee_name,
    payee_ref: payload.payee_type === 'employee'
      ? (payload.employee_no ?? payload.payee_ref)
      : (payload.payee_id ?? payload.payee_ref ?? defaults.payee_id),
    payee_id: payload.payee_id ?? defaults.payee_id,
    bank_name: payload.bank_name ?? defaults.bank_name,
    bank_branch_name: payload.bank_branch_name ?? defaults.bank_branch_name,
    bank_account_no: payload.bank_account_no ?? defaults.bank_account_no,
    tax_no: payload.tax_no ?? defaults.tax_no ?? defaults.payee_tax_no,
    id_card_no: payload.id_card_no ?? defaults.id_card_no ?? defaults.payee_id_card_no,
    prepayment_no: payload.prepayment_no ?? defaults.prepayment_no,
    lines: (payload.lines || []).map((line) => ({
      ...line,
      invoice_files: line.invoice_files || []
    })),
    other_attachments: payload.other_attachments || [],
    remark: payload.remark || ''
  });
}

function applyPrepaymentAppendDefaults(form, meta, variantConfig) {
  const defaults = variantConfig?.defaults || {};
  const requiredAmount = Number(variantConfig?.requiredAmount || 0);
  const defaultBudgetOrg = defaults.applicant_dept_code || meta?.default_budget_org_code;
  const categoryId = defaults.category_id;
  form.setFieldsValue({
    expense_company_id: defaults.expense_company_id ?? meta?.default_expense_company_id,
    applicant_dept_code: defaults.applicant_dept_code ?? meta?.default_applicant_dept_code,
    category_id: categoryId,
    reason: defaults.reason,
    urgency: defaults.urgency || 'normal',
    expense_occurrence_month: defaults.expense_occurrence_month
      ? dayjs(`${defaults.expense_occurrence_month}-01`)
      : undefined,
    payee_type: defaults.payee_type || 'supplier',
    payee_name: defaults.payee_name,
    payee_ref: defaults.payee_id,
    payee_id: defaults.payee_id,
    bank_name: defaults.bank_name,
    bank_branch_name: defaults.bank_branch_name,
    bank_account_no: defaults.bank_account_no,
    tax_no: defaults.tax_no || defaults.payee_tax_no,
    id_card_no: defaults.id_card_no || defaults.payee_id_card_no,
    prepayment_no: defaults.prepayment_no,
    lines: [{
      expense_detail: '',
      budget_org_code: defaultBudgetOrg,
      subitem_id: undefined,
      invoice_type: 'general',
      invoice_files: [],
      invoice_amount: requiredAmount > 0 ? requiredAmount : undefined,
      actual_amount: requiredAmount > 0 ? requiredAmount : undefined
    }],
    other_attachments: [],
    remark: ''
  });
}

export default function ExpenseClaimForm({ form, variant, variantConfig }) {
  const isPrepaymentAppend = variant === 'prepayment_append';
  const isFromBusinessTrip = variant === 'from_business_trip';
  const isFromMatInbound = variant === 'from_mat_inbound';
  const lockHeaderFields = isFromBusinessTrip || isFromMatInbound;
  const lockBudgetSubject = lockHeaderFields && Boolean(variantConfig?.prefill?.budget_subject_locked);
  const lockedSubitemId = lockBudgetSubject ? variantConfig?.prefill?.subitem_id : undefined;
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [meta, setMeta] = useState(null);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const payeeSearchSeqRef = useRef(0);
  const [payeeSearching, setPayeeSearching] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [ocrLoadingKey, setOcrLoadingKey] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const ocrAbortControllerRef = useRef(null);
  const ocrMountedRef = useRef(true);
  const prepaymentAppendFormInitRef = useRef(false);
  const businessTripFormInitRef = useRef(false);
  const matInboundFormInitRef = useRef(false);

  const payeeType = Form.useWatch('payee_type', form);
  const relatedApprovalId = Form.useWatch('related_approval_id', form);
  const relatedApprovalBizType = Form.useWatch('related_approval_biz_type', form);
  const relatedInboundIds = Form.useWatch('related_inbound_ids', form) || [];
  const inboundTotalAmount = Number(
    Form.useWatch('inbound_total_amount', form)
    ?? variantConfig?.prefill?.inbound_total_amount
    ?? 0
  );
  const payeeRef = Form.useWatch('payee_ref', form);
  const payeeName = Form.useWatch('payee_name', form);
  const payeeLockedFromLibrary = payeeType !== 'employee' && Boolean(payeeRef) && !isFromMatInbound;
  const selectedBankName = Form.useWatch('bank_name', form);
  const applicantDeptCode = Form.useWatch('applicant_dept_code', form);
  const budgetOrgCode = Form.useWatch('budget_org_code', form);
  const watchedCategoryId = Form.useWatch('category_id', form);
  const categoryId = isPrepaymentAppend
    ? (watchedCategoryId ?? variantConfig?.defaults?.category_id)
    : watchedCategoryId;
  const expenseOccurrenceMonth = Form.useWatch('expense_occurrence_month', form);
  const reason = Form.useWatch('reason', form);
  const lines = Form.useWatch('lines', form) || [];
  const otherAttachments = Form.useWatch('other_attachments', form) || [];

  const budgetOrgTreePropsForSplit = useMemo(
    () => buildBudgetOrgTreeSelectProps(meta?.organizations || [], { style: { width: '100%' } }),
    [meta?.organizations]
  );

  const budgetOrgTreeProps = useMemo(
    () => ({
      ...budgetOrgTreePropsForSplit,
      treeData: [
        ...(budgetOrgTreePropsForSplit.treeData || []),
        {
          title: '多部门分摊',
          value: MULTI_DEPT_BUDGET_ORG_CODE,
          key: MULTI_DEPT_BUDGET_ORG_CODE,
          isLeaf: true
        }
      ]
    }),
    [budgetOrgTreePropsForSplit]
  );

  const selectedCategory = useMemo(
    () => findCategoryById(meta?.categories, categoryId),
    [meta?.categories, categoryId]
  );

  const budgetPeriodPreview = useMemo(
    () => resolveBudgetPeriodLabel(selectedCategory?.period_type, expenseOccurrenceMonth),
    [selectedCategory?.period_type, expenseOccurrenceMonth]
  );

  const invoiceTotal = useMemo(
    () => (lines || []).reduce((sum, line) => sum + Number(line?.invoice_amount || 0), 0),
    [lines]
  );
  const actualTotal = useMemo(
    () => (lines || []).reduce((sum, line) => sum + Number(line?.actual_amount || 0), 0),
    [lines]
  );
  const invoiceAttachmentCount = useMemo(
    () => (lines || []).reduce((sum, line) => sum + (line?.invoice_files?.length || 0), 0),
    [lines]
  );
  const attachmentCount = invoiceAttachmentCount + (otherAttachments?.length || 0);

  const applicantDeptName = useMemo(() => {
    const hit = (meta?.applicant_dept_options || []).find((item) => item.org_code === applicantDeptCode);
    if (hit?.org_name) return hit.org_name;
    return findOrgName(meta?.organizations || [], applicantDeptCode);
  }, [meta, applicantDeptCode]);

  const subjectPreview = useMemo(() => buildExpenseClaimSubject({
    applicantDeptName,
    applicantName: meta?.applicant_name || '',
    expenseOccurrenceMonth: expenseOccurrenceMonth?.format?.('YYYY-MM') ?? expenseOccurrenceMonth,
    reason
  }), [applicantDeptName, meta, expenseOccurrenceMonth, reason]);

  const remarkRequired = useMemo(
    () => (lines || []).some((line) => lineHasAmountBelowInvoice(line)),
    [lines]
  );

  const loadMeta = useCallback(async () => {
    try {
      setLoadingMeta(true);
      const res = await api.get('/expense/claims/meta');
      if (!res.data.success) return;
      const data = res.data.data || {};
      setMeta(data);
      if (isPrepaymentAppend) {
        if (!prepaymentAppendFormInitRef.current) {
          if (variantConfig?.initialPayload) {
            hydratePrepaymentAppendForm(form, variantConfig.initialPayload, variantConfig.defaults || {});
          } else if (variantConfig?.defaults) {
            applyPrepaymentAppendDefaults(form, data, variantConfig);
          }
          prepaymentAppendFormInitRef.current = true;
        }
        const defaults = variantConfig?.defaults || {};
        if (defaults.expense_company_id) {
          const option = {
            id: defaults.expense_company_id,
            label: defaults.expense_company_name,
            short_name: defaults.expense_company_short_name || defaults.expense_company_name,
            company_name: defaults.expense_company_full_name || defaults.expense_company_name
          };
          setExpenseCompanyNamesSnapshot(collectExpenseCompanyNamesFromOption(option));
        }
        return;
      }

      if (isFromBusinessTrip) {
        if (!businessTripFormInitRef.current && variantConfig?.prefill) {
          applyBusinessTripExpensePrefill(form, variantConfig.prefill, data);
          businessTripFormInitRef.current = true;
        }
        const defaultCompanyId = data.default_expense_company_id ?? data.expense_company_id ?? undefined;
        const defaultCompanyOption = (data.expense_company_options || []).find(
          (item) => String(item.id) === String(defaultCompanyId)
        );
        setExpenseCompanyNamesSnapshot(collectExpenseCompanyNamesFromOption(defaultCompanyOption));
        return;
      }

      if (isFromMatInbound) {
        if (!matInboundFormInitRef.current && variantConfig?.prefill) {
          applyMatInboundExpensePrefill(form, variantConfig.prefill, data);
          matInboundFormInitRef.current = true;
        }
        const defaultCompanyId = data.default_expense_company_id ?? data.expense_company_id ?? undefined;
        const defaultCompanyOption = (data.expense_company_options || []).find(
          (item) => String(item.id) === String(defaultCompanyId)
        );
        setExpenseCompanyNamesSnapshot(collectExpenseCompanyNamesFromOption(defaultCompanyOption));
        return;
      }

      const existing = form.getFieldsValue(true);
      const hasDraftValues = Boolean(
        existing.category_id
        || String(existing.reason || '').trim()
        || existing.expense_company_id
        || existing.payee_ref
        || String(existing.payee_name || '').trim()
        || (Array.isArray(existing.lines) && existing.lines.some((line) => (
          String(line?.expense_detail || '').trim()
          || (line?.invoice_files || []).length
          || line?.invoice_amount != null
          || line?.actual_amount != null
        )))
      );

      if (hasDraftValues) return;

      const defaultPayee = data.default_employee_payee;
      const defaultBudgetOrg = data.default_budget_org_code || data.default_applicant_dept_code;
      const defaultCompanyId = data.default_expense_company_id ?? data.expense_company_id ?? undefined;
      const defaultCompanyOption = (data.expense_company_options || []).find(
        (item) => String(item.id) === String(defaultCompanyId)
      );
      setExpenseCompanyNamesSnapshot(collectExpenseCompanyNamesFromOption(defaultCompanyOption));
      form.setFieldsValue({
        expense_company_id: defaultCompanyId,
        applicant_dept_code: data.default_applicant_dept_code,
        category_id: undefined,
        payee_type: 'employee',
        payee_ref: defaultPayee?.employee_no,
        payee_name: defaultPayee?.name,
        bank_name: defaultPayee?.bank_name,
        bank_account_no: defaultPayee?.bank_account_no,
        urgency: 'normal',
        lines: [{
          expense_detail: '',
          budget_org_code: defaultBudgetOrg,
          subitem_id: undefined,
          invoice_type: 'general',
          invoice_files: [],
          invoice_amount: undefined,
          actual_amount: undefined
        }]
      });
    } catch (e) {
      message.error(e.response?.data?.message || '加载报销表单失败');
    } finally {
      setLoadingMeta(false);
    }
  }, [form, isFromBusinessTrip, isFromMatInbound, isPrepaymentAppend, variantConfig]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const syncExpenseCompanyNames = useCallback((companyId, options = meta?.expense_company_options) => {
    if (!companyId) {
      setExpenseCompanyNamesSnapshot(null);
      return;
    }
    const option = (options || []).find((item) => String(item.id) === String(companyId));
    setExpenseCompanyNamesSnapshot(collectExpenseCompanyNamesFromOption(option));
  }, [meta?.expense_company_options]);

  useEffect(() => {
    expenseCompanyOptionsRef.current = meta?.expense_company_options || [];
    const companyId = form.getFieldValue('expense_company_id');
    if (companyId && meta?.expense_company_options?.length) {
      syncExpenseCompanyNames(companyId);
    }
  }, [form, meta?.expense_company_options, syncExpenseCompanyNames]);

  useEffect(() => {
    ocrMountedRef.current = true;
    return () => {
      ocrMountedRef.current = false;
      ocrAbortControllerRef.current?.abort();
      ocrAbortControllerRef.current = null;
    };
  }, []);

  const searchPayees = useCallback(async (keyword, payeeTypeOverride) => {
    const type = payeeTypeOverride || form.getFieldValue('payee_type') || 'employee';
    const seq = ++payeeSearchSeqRef.current;
    try {
      setPayeeSearching(true);
      const res = await api.get('/expense/payees/search', {
        params: { payee_type: type, keyword, limit: 20 }
      });
      if (seq !== payeeSearchSeqRef.current) return;
      if (res.data.success) {
        setPayeeOptions(res.data.data || []);
      }
    } catch {
      if (seq === payeeSearchSeqRef.current) {
        setPayeeOptions([]);
      }
    } finally {
      if (seq === payeeSearchSeqRef.current) {
        setPayeeSearching(false);
      }
    }
  }, [form]);

  const searchBranches = useCallback(async (keyword) => {
    const bankName = form.getFieldValue('bank_name');
    if (!bankName) {
      setBranchOptions([]);
      return;
    }
    try {
      const res = await api.get('/expense/claims/meta/bank-branches', {
        params: { keyword, bank_name: bankName }
      });
      if (res.data.success) {
        setBranchOptions((res.data.data || []).map((b) => ({
          value: b.branch_name,
          label: b.branch_name
        })));
      }
    } catch {
      setBranchOptions([]);
    }
  }, [form]);

  const handleBankChange = (bankName) => {
    form.setFieldsValue({ bank_name: bankName, bank_branch_name: undefined });
    setBranchOptions([]);
  };

  const clearExternalPayeeFields = useCallback(() => {
    form.setFieldsValue({
      payee_ref: undefined,
      payee_name: undefined,
      bank_name: undefined,
      bank_branch_name: undefined,
      bank_account_no: undefined,
      id_card_no: undefined,
      tax_no: undefined
    });
    setBranchOptions([]);
  }, [form]);

  const clearExternalPayeeLinkedFields = useCallback(() => {
    form.setFieldsValue({
      payee_ref: undefined,
      bank_name: undefined,
      bank_branch_name: undefined,
      bank_account_no: undefined,
      id_card_no: undefined,
      tax_no: undefined
    });
    setBranchOptions([]);
  }, [form]);

  const applyPayeeFromLibrary = (item) => {
    if (!item) return;
    form.setFieldsValue({
      payee_ref: item.id,
      payee_name: item.name,
      bank_name: item.bank_name,
      bank_branch_name: item.bank_branch_name,
      bank_account_no: item.bank_account_no,
      id_card_no: item.id_card_no,
      tax_no: item.tax_no
    });
    if (item.bank_name) {
      searchBranches('');
    }
  };

  const payeeOptionsForType = useMemo(() => {
    if (payeeType === 'employee') {
      return payeeOptions.filter((item) => item.payee_type === 'employee');
    }
    return payeeOptions.filter((item) => item.payee_type === payeeType);
  }, [payeeOptions, payeeType]);

  const handleExternalPayeeNameChange = (value) => {
    const text = String(value ?? '').trim();
    if (!text) {
      clearExternalPayeeFields();
      return;
    }
    const matched = payeeOptionsForType.find((item) => item.name === text);
    if (matched) {
      if (form.getFieldValue('payee_ref') !== matched.id) {
        applyPayeeFromLibrary(matched);
      }
      return;
    }
    if (form.getFieldValue('payee_ref')) {
      clearExternalPayeeLinkedFields();
    }
  };

  const prevPayeeTypeRef = useRef(null);

  useEffect(() => {
    if (payeeType !== 'employee' || !payeeRef || !payeeName) return;
    setPayeeOptions((prev) => {
      if (prev.some((item) => String(item.employee_no || item.id) === String(payeeRef))) {
        return prev;
      }
      return [{
        payee_type: 'employee',
        employee_no: payeeRef,
        name: payeeName,
        label: payeeName,
        bank_name: form.getFieldValue('bank_name'),
        bank_account_no: form.getFieldValue('bank_account_no')
      }, ...prev];
    });
  }, [payeeType, payeeRef, payeeName, form]);

  useEffect(() => {
    const prevPayeeType = prevPayeeTypeRef.current;
    prevPayeeTypeRef.current = payeeType;

    if (prevPayeeType == null || prevPayeeType === payeeType) {
      searchPayees('', payeeType);
      return;
    }

    payeeSearchSeqRef.current += 1;
    setPayeeOptions([]);

    const clearedPayeeFields = {
      payee_ref: undefined,
      payee_name: undefined,
      bank_name: undefined,
      bank_branch_name: undefined,
      bank_account_no: undefined,
      id_card_no: undefined,
      tax_no: undefined
    };

    if (payeeType === 'employee') {
      const defaultPayee = meta?.default_employee_payee;
      form.setFieldsValue({
        ...clearedPayeeFields,
        payee_ref: defaultPayee?.employee_no,
        payee_name: defaultPayee?.name,
        bank_name: defaultPayee?.bank_name,
        bank_account_no: defaultPayee?.bank_account_no
      });
    } else {
      form.setFieldsValue(clearedPayeeFields);
      setBranchOptions([]);
    }

    searchPayees('', payeeType);
  }, [payeeType, meta, form, searchPayees]);

  useEffect(() => {
    if (payeeType === 'employee') return;
    api.get('/expense/claims/meta/banks').then((res) => {
      if (res.data.success) {
        setBankOptions((res.data.data || []).map((b) => ({
          value: b.bank_name,
          label: b.bank_name
        })));
      }
    }).catch(() => setBankOptions([]));
  }, [payeeType]);

  const handleEmployeePayeeSelect = (value, option) => {
    const item = option?.item;
    if (!item) return;
    form.setFieldsValue({
      payee_ref: item.employee_no || value,
      payee_name: item.name,
      bank_name: item.bank_name,
      bank_account_no: item.bank_account_no
    });
  };

  const uploadAttachment = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/expense/claims/upload-attachment', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (!res.data.success) throw new Error(res.data.message || '上传失败');
    return res.data.data;
  };

  const handleInvoiceUpload = async (file, fieldName, uploadKey) => {
    const loadingKey = uploadKey ?? fieldName;
    const invoiceType = form.getFieldValue(['lines', fieldName, 'invoice_type']);
    if (invoiceType === 'other') {
      try {
        setOcrLoadingKey(loadingKey);
        const data = await uploadAttachment(file);
        const linesValue = form.getFieldValue('lines') || [];
        const nextLines = [...linesValue];
        const line = { ...(nextLines[fieldName] || {}) };
        line.invoice_files = [{
          file_name: data.file_name || file.name,
          file_path: data.file_path
        }];
        line.ocr_raw = null;
        line.vat_verify = null;
        line.invoice_no = null;
        nextLines[fieldName] = line;
        form.setFieldsValue({ lines: nextLines });
        form.validateFields([['lines', fieldName, 'invoice_files']]).catch(() => {});
        message.success('附件已上传');
      } catch (e) {
        message.error(e.response?.data?.message || e.message || '上传失败');
      } finally {
        setOcrLoadingKey(null);
      }
      return Upload.LIST_IGNORE;
    }
    ocrAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    ocrAbortControllerRef.current = abortController;

    try {
      setOcrLoadingKey(loadingKey);
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/expense/claims/invoice-ocr', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: INVOICE_OCR_TIMEOUT_MS,
        signal: abortController.signal
      });
      if (!ocrMountedRef.current) return Upload.LIST_IGNORE;
      if (!res.data.success) throw new Error(res.data.message || '识别失败');
      const data = res.data.data || {};
      const linesValue = form.getFieldValue('lines') || [];
      if (data.invoice_no) {
        const target = String(data.invoice_no).trim().toUpperCase();
        const dupIndex = linesValue.findIndex((row, index) => (
          index !== fieldName
          && String(row?.invoice_no || '').trim().toUpperCase() === target
        ));
        if (dupIndex >= 0) {
          message.error(`发票号 ${data.invoice_no} 与第 ${dupIndex + 1} 行重复，请勿重复上传`);
          return Upload.LIST_IGNORE;
        }
      }
      const nextLines = [...linesValue];
      const line = { ...(nextLines[fieldName] || {}) };
      line.invoice_files = [{
        file_name: data.file_name || file.name,
        file_path: data.file_path
      }];
      if (data.invoice_no) line.invoice_no = data.invoice_no;
      const invoiceAmount = data.total_amount ?? data.invoice_amount;
      if (invoiceAmount != null) line.invoice_amount = invoiceAmount;
      if (invoiceAmount != null && line.actual_amount == null) {
        line.actual_amount = invoiceAmount;
      }
      line.ocr_raw = data.raw_kv || null;
      line.vat_verify = data.vat_verify || null;
      if (!isPrepaymentAppend) {
        const buyerName = formatInvoiceBuyerDisplayText({
          ocrRaw: line.ocr_raw,
          verify: line.vat_verify
        });
        if (!buyerName) {
          message.error('未能识别发票购买方（我方单位）名称，请重新上传清晰、完整的发票影像');
          return Upload.LIST_IGNORE;
        }
      }
      nextLines[fieldName] = line;
      form.setFieldsValue({ lines: nextLines });
      form.setFieldValue(['lines', fieldName, 'ocr_raw'], line.ocr_raw);
      form.setFieldValue(['lines', fieldName, 'vat_verify'], line.vat_verify);
      form.validateFields([
        ['lines', fieldName, 'invoice_files'],
        ['lines', fieldName, 'invoice_amount'],
        ['lines', fieldName, 'actual_amount']
      ]).catch(() => {});
      if (res.data.partial) {
        message.warning(res.data.message || '自动识别未完全成功，附件已保存，请手工填写发票号与金额');
      } else if (data.llm_assisted && data.ocr_message) {
        message.success(data.ocr_message);
      } else if (data.invoice_no || invoiceAmount != null) {
        message.success('发票识别完成');
      } else {
        message.warning('附件已保存，请手工填写发票号与金额');
      }
    } catch (e) {
      if (isInvoiceOcrAbortError(e) || !ocrMountedRef.current) {
        return Upload.LIST_IGNORE;
      }
      const isTimeout = e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '');
      message.error(
        isTimeout
          ? '发票识别超时，请稍后重试或手工填写'
          : (e.response?.data?.message || e.message || '发票识别失败')
      );
    } finally {
      if (ocrAbortControllerRef.current === abortController) {
        ocrAbortControllerRef.current = null;
      }
      if (ocrMountedRef.current) {
        setOcrLoadingKey(null);
      }
    }
    return Upload.LIST_IGNORE;
  };

  const removeInvoiceFile = (lineIndex, fileIndex) => {
    const linesValue = form.getFieldValue('lines') || [];
    const nextLines = [...linesValue];
    const line = { ...(nextLines[lineIndex] || {}) };
    const invoiceType = line.invoice_type;
    line.invoice_files = (line.invoice_files || []).filter((_, index) => index !== fileIndex);
    if (!line.invoice_files.length) {
      line.vat_verify = null;
      line.ocr_raw = null;
      line.invoice_no = null;
      if (invoiceType !== 'other') {
        line.invoice_amount = null;
        line.actual_amount = null;
      }
    }
    nextLines[lineIndex] = line;
    form.setFieldsValue({ lines: nextLines });
    form.setFieldValue(['lines', lineIndex, 'ocr_raw'], line.ocr_raw);
    form.setFieldValue(['lines', lineIndex, 'vat_verify'], line.vat_verify);
    form.validateFields([
      ['lines', lineIndex, 'invoice_files'],
      ['lines', lineIndex, 'invoice_amount'],
      ['lines', lineIndex, 'actual_amount']
    ]).catch(() => {});
  };

  const handleOtherAttachmentUpload = async (file) => {
    try {
      const data = await uploadAttachment(file);
      const current = form.getFieldValue('other_attachments') || [];
      form.setFieldsValue({
        other_attachments: [...current, {
          file_name: data.file_name || file.name,
          file_path: data.file_path
        }]
      });
      message.success('附件已上传');
    } catch (e) {
      message.error(e.response?.data?.message || e.message || '上传失败');
    }
    return Upload.LIST_IGNORE;
  };

  const payeeSelectOptions = payeeOptionsForType.map((item) => ({
    value: item.employee_no || item.id || item.name,
    label: item.label || item.name,
    item
  }));

  return (
    <Form form={form} layout="vertical" className="expense-claim-form" style={{ opacity: loadingMeta ? 0.6 : 1 }}>
      {isPrepaymentAppend && (
        <>
          <Form.Item name="category_id" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="expense_company_id" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="applicant_dept_code" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="reason" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="urgency" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="payee_type" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="payee_name" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="payee_ref" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="payee_id" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="bank_name" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="bank_branch_name" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="bank_account_no" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="tax_no" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="id_card_no" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <div className="prepayment-append-readonly-banner">
            <div>单据类型：{variantConfig?.defaults?.doc_type || '费用报销-预付款追加'}</div>
            <div>预付款单号：{variantConfig?.defaults?.prepayment_no || '—'}</div>
            <div>预算一级科目：{selectedCategory?.name || variantConfig?.defaults?.category_name || '—'}</div>
            <div>
              追加金额须等于：
              <strong> ¥{Number(variantConfig?.requiredAmount || 0).toFixed(2)}</strong>
            </div>
          </div>
        </>
      )}

      {!isPrepaymentAppend && (
      <FormSection title="基本信息">
      <Row gutter={GUTTER}>
        <FieldCol>
          <Form.Item
            name="expense_company_id"
            label="费用承担公司"
            rules={[{ required: true, message: '请选择费用承担公司' }]}
            extra={
              meta?.employee_company_id
              && meta.employee_company_id !== meta?.default_expense_company_id
              && meta?.employee_company_name
                ? `员工所属公司为「${meta.employee_company_name}」（非公开，请从公开公司中选择）`
                : undefined
            }
          >
            <Select
              placeholder="选择费用承担公司"
              showSearch
              optionFilterProp="label"
              loading={loadingMeta}
              onChange={(value) => syncExpenseCompanyNames(value)}
              options={(meta?.expense_company_options || []).map((item) => ({
                value: item.id,
                label: item.label || item.company_name
              }))}
            />
          </Form.Item>
        </FieldCol>
        <FieldCol>
          <Form.Item label="单据类型">
            <Input value={meta?.doc_type || '费用报销'} disabled />
          </Form.Item>
        </FieldCol>
        <FieldCol>
          <Form.Item
            name="applicant_dept_code"
            label="申请人部门"
            rules={[{ required: true, message: '请选择申请人部门' }]}
          >
            <Select
              placeholder="选择部门"
              options={(meta?.applicant_dept_options || []).map((item) => ({
                value: item.org_code,
                label: item.org_name
              }))}
            />
          </Form.Item>
        </FieldCol>
        <FieldCol>
          <Form.Item
            name="category_id"
            label="预算一级科目"
            rules={[{ required: true, message: '请选择预算一级科目' }]}
          >
            <Select
              placeholder="选择一级科目"
              showSearch
              optionFilterProp="label"
              disabled={lockBudgetSubject}
              onChange={() => {
                if (lockBudgetSubject) return;
                const currentLines = form.getFieldValue('lines') || [];
                form.setFieldsValue({
                  lines: currentLines.map((line) => ({ ...line, subitem_id: undefined }))
                });
              }}
              options={(meta?.categories || []).map((item) => ({
                value: item.id,
                label: `${item.name}${item.period_type_label ? `（${item.period_type_label}）` : ''}`
              }))}
            />
          </Form.Item>
        </FieldCol>
        <FieldCol>
          <Form.Item
            name="expense_occurrence_month"
            label="费用产生时间"
            rules={[
              { required: true, message: '请选择费用产生时间' },
              {
                validator: (_, value) => {
                  const message = validateExpenseOccurrenceMonthValue(value);
                  if (message) return Promise.reject(new Error(message));
                  return Promise.resolve();
                }
              }
            ]}
            extra={budgetPeriodPreview ? `所属预算周期：${budgetPeriodPreview}` : undefined}
          >
            <DatePicker
              picker="month"
              style={{ width: '100%' }}
              format="YYYY-MM"
              disabled={lockHeaderFields}
              disabledDate={isExpenseOccurrenceMonthDisabled}
            />
          </Form.Item>
        </FieldCol>
        <FieldCol>
          <Form.Item name="urgency" label="紧急程度" rules={[{ required: true, message: '请选择紧急程度' }]}>
            <Radio.Group options={URGENCY_OPTIONS} />
          </Form.Item>
        </FieldCol>
        <FieldCol span={COL2}>
          <Form.Item name="reason" label="事由" rules={[{ required: true, message: '请填写事由' }]}>
            <Input placeholder="简要说明报销事由" disabled={lockHeaderFields} />
          </Form.Item>
        </FieldCol>
        <FieldCol span={COL2}>
          <Form.Item label="主题">
            <Input value={subjectPreview || '填写部门、费用产生时间与事由后自动生成'} disabled />
          </Form.Item>
        </FieldCol>
        <FieldCol>
          <Form.Item
            name="related_approval_no"
            label={relatedApprovalBizType === 'mat_inbound' ? '关联入库单' : '关联审批单'}
          >
            <RelatedApprovalNoField
              disabled={lockHeaderFields}
              relatedApprovalId={relatedApprovalId}
              relatedApprovalBizType={relatedApprovalBizType}
              relatedInboundIds={Array.isArray(relatedInboundIds) ? relatedInboundIds : []}
            />
          </Form.Item>
          <Form.Item name="related_approval_id" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="related_approval_biz_type" hidden noStyle>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="related_inbound_ids" hidden noStyle preserve>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="inbound_total_amount" hidden noStyle preserve>
            <Input type="hidden" />
          </Form.Item>
        </FieldCol>
      </Row>
      </FormSection>
      )}

      {!isPrepaymentAppend && (
      <FormSection title="收款与税务">
      <Row gutter={GUTTER}>
        <FieldCol span={FULL}>
          <Form.Item name="payee_type" label="收款方类型" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
            <Radio.Group options={PAYEE_TYPE_OPTIONS} disabled={isFromBusinessTrip} />
          </Form.Item>
        </FieldCol>
        {payeeType === 'employee' ? (
          <FieldCol>
            <Form.Item
              name="payee_ref"
              label="收款方名称"
              rules={[{ required: true, message: '请选择员工收款方' }]}
            >
              <Select
                showSearch
                allowClear
                disabled={isFromBusinessTrip}
                placeholder="搜索数据权限范围内员工"
                filterOption={false}
                loading={payeeSearching}
                listHeight={256}
                popupClassName="expense-payee-dropdown"
                onSearch={(keyword) => searchPayees(keyword, 'employee')}
                onSelect={handleEmployeePayeeSelect}
                options={payeeSelectOptions}
                notFoundContent={payeeSearching ? '搜索中...' : '无匹配员工'}
              />
            </Form.Item>
            <Form.Item name="payee_name" hidden><Input /></Form.Item>
          </FieldCol>
        ) : (
          <FieldCol>
            <Form.Item
              name="payee_name"
              label="收款方名称"
              rules={[{ required: true, message: '请填写或选择收款方名称' }]}
            >
              <AutoComplete
                allowClear
                listHeight={256}
                popupClassName="expense-payee-dropdown"
                options={payeeOptionsForType.map((item) => ({
                  value: item.name,
                  label: item.name,
                  item
                }))}
                onSearch={(keyword) => searchPayees(keyword, payeeType)}
                onSelect={(_, option) => applyPayeeFromLibrary(option.item)}
                onChange={handleExternalPayeeNameChange}
                placeholder="输入或搜索收款方名称"
                notFoundContent={payeeSearching ? '搜索中...' : '无匹配，可手动输入新收款方'}
              />
            </Form.Item>
            <Form.Item name="payee_ref" hidden><Input /></Form.Item>
          </FieldCol>
        )}
        <FieldCol>
          <Form.Item
            name="bank_name"
            label="开户银行"
            rules={[{ required: true, message: payeeType === 'employee' ? '员工档案缺少开户银行' : '请选择开户银行' }]}
          >
            {payeeType === 'employee' || payeeLockedFromLibrary ? (
              <Input placeholder="开户银行" disabled />
            ) : (
              <Select
                showSearch
                allowClear
                placeholder="请选择开户银行"
                optionFilterProp="label"
                options={bankOptions}
                onChange={handleBankChange}
                notFoundContent="暂无银行主数据，请先导入支行库"
              />
            )}
          </Form.Item>
        </FieldCol>
        {payeeType !== 'employee' && (
          <FieldCol>
            <Form.Item
              name="bank_branch_name"
              label="支行名称"
              rules={[{ required: true, message: '请选择支行名称' }]}
            >
              {payeeLockedFromLibrary ? (
                <Input placeholder="支行名称" disabled />
              ) : (
                <Select
                  showSearch
                  allowClear
                  placeholder="请先选择开户银行，再搜索支行"
                  filterOption={false}
                  disabled={!selectedBankName}
                  onSearch={searchBranches}
                  onFocus={() => searchBranches('')}
                  options={branchOptions}
                  notFoundContent="暂无匹配支行"
                />
              )}
            </Form.Item>
          </FieldCol>
        )}
        <FieldCol>
          <Form.Item
            name="bank_account_no"
            label="银行账号"
            rules={[{ required: true, message: '请填写银行账号' }]}
          >
            <Input placeholder="银行账号" disabled={payeeType === 'employee' || payeeLockedFromLibrary} />
          </Form.Item>
        </FieldCol>
        {payeeType === 'external_person' && (
          <FieldCol>
            <Form.Item
              name="id_card_no"
              label="收款人身份证号"
              rules={[
                { required: true, message: '外部个人须填写身份证号' },
                { validator: createPayeeFieldValidator(validateChineseIdCard) }
              ]}
            >
              <Input placeholder="身份证号" disabled={payeeLockedFromLibrary} />
            </Form.Item>
          </FieldCol>
        )}
        {payeeType === 'supplier' && (
          <FieldCol>
            <Form.Item
              name="tax_no"
              label="对方税号"
              rules={[
                { required: true, message: '供应商须填写税号' },
                { validator: createPayeeFieldValidator(validateSupplierTaxNo) }
              ]}
            >
              <Input placeholder="统一社会信用代码/税号" disabled={payeeLockedFromLibrary} />
            </Form.Item>
          </FieldCol>
        )}
      </Row>
      </FormSection>
      )}

      <FormSection title="报销明细">
      <Form.List name="lines">
        {(fields, { add, remove }) => (
          <>
            <div className="expense-line-list">
              {fields.map((field, index) => (
                <ExpenseLineCard
                  key={field.key}
                  field={field}
                  index={index}
                  form={form}
                  canRemove={fields.length > 1}
                  onRemove={() => remove(field.name)}
                  selectedCategory={selectedCategory}
                  budgetOrgTreeProps={budgetOrgTreeProps}
                  budgetOrgTreePropsForSplit={budgetOrgTreePropsForSplit}
                  categoryId={categoryId}
                  expenseOccurrenceMonth={expenseOccurrenceMonth}
                  ocrLoadingKey={ocrLoadingKey}
                  onInvoiceUpload={handleInvoiceUpload}
                  onRemoveFile={removeInvoiceFile}
                  onPreview={setPreviewFile}
                  skipInvoiceBuyerValidation={isPrepaymentAppend}
                  subitemDisabled={lockBudgetSubject}
                />
              ))}
            </div>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              className="expense-line-add-btn"
              onClick={() => add({
                expense_detail: '',
                budget_org_code: meta?.default_budget_org_code || meta?.default_applicant_dept_code,
                subitem_id: lockedSubitemId,
                invoice_type: 'general',
                invoice_files: [],
                invoice_amount: undefined,
                actual_amount: undefined
              })}
            >
              新增费用明细
            </Button>
          </>
        )}
      </Form.List>

      {isFromMatInbound && inboundTotalAmount > 0 && (
        <div className="expense-claim-inbound-hint">
          <Text type="secondary">
            关联入库单合计 ¥{inboundTotalAmount.toFixed(2)}，提交时实付合计须与此金额一致
          </Text>
        </div>
      )}

      <div className="expense-claim-summary">
        <div className="expense-claim-summary-item">
          <Text type="secondary">发票合计</Text>
          <Text strong>¥{invoiceTotal.toFixed(2)}</Text>
        </div>
        <div className="expense-claim-summary-item">
          <Text type="secondary">实付合计</Text>
          <Text strong className="expense-claim-summary-total">¥{actualTotal.toFixed(2)}</Text>
        </div>
        <div className="expense-claim-summary-item">
          <Text type="secondary">金额大写</Text>
          <Text>{amountToChinese(actualTotal) || '—'}</Text>
        </div>
        <div className="expense-claim-summary-item">
          <Text type="secondary">附件张数</Text>
          <Text strong>{attachmentCount}</Text>
        </div>
      </div>

      {!isPrepaymentAppend && (
      <Row gutter={GUTTER}>
        <FieldCol span={FULL}>
          <Form.Item label="其他附件" style={{ marginBottom: 8 }}>
            <Upload beforeUpload={handleOtherAttachmentUpload} showUploadList={false} multiple>
              <Button icon={<UploadOutlined />}>上传附件</Button>
            </Upload>
            {(otherAttachments || []).length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                {otherAttachments.map((file, index) => (
                  <div key={`${file.file_path}-${index}`}>{file.file_name}</div>
                ))}
              </div>
            )}
          </Form.Item>
          <Form.Item name="other_attachments" hidden initialValue={[]}>
            <Input hidden />
          </Form.Item>
        </FieldCol>
        <FieldCol span={FULL}>
          <Form.Item
            name="remark"
            label="备注"
            rules={remarkRequired ? [{ required: true, message: '存在发票与实付金额不一致的明细，请填写备注说明' }] : []}
          >
            <Input placeholder={remarkRequired ? '请说明各明细发票与实付金额差异原因' : '选填'} />
          </Form.Item>
        </FieldCol>
      </Row>
      )}
      {isPrepaymentAppend && (
        <Row gutter={GUTTER}>
          <FieldCol span={FULL}>
            <Form.Item
              name="remark"
              label="备注"
              rules={remarkRequired ? [{ required: true, message: '存在发票与实付金额不一致的明细，请填写备注说明' }] : []}
            >
              <Input placeholder={remarkRequired ? '请说明各明细发票与实付金额差异原因' : '选填'} />
            </Form.Item>
          </FieldCol>
        </Row>
      )}
      </FormSection>

      <InvoiceFilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
    </Form>
  );
}
